import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { Children, memo, useCallback, useEffect, useMemo, useState, } from 'react';
import { totalBorderOffset } from '../../styles/border';
import styleUtils from '../../styles/styleUtils';
import theme from '../../styles/theme';
import { IconButton } from '../IconButton';
import useMediaQueryState from '../MediaQuery/useMediaQueryState';
import { Picker } from '../Picker';
import { ThumbnailCarouselContext } from './ThumbnailCarouselContext';
const carouselContainerSize = 456;
const slideSizes = {
    medium: 96 - totalBorderOffset,
    small: 70 - totalBorderOffset,
};
const slideGaps = {
    medium: theme.spacing.x6,
    small: theme.spacing.x4,
};
const scrollDirections = {
    backward: 'backward',
    forward: 'forward',
    none: 'none',
};
const ThumbnailCarousel = ({ children, name, onChange, orientation, value, }) => {
    const extraSmallMediaQueryState = (useMediaQueryState({
        mediaQuerySize: 'extraSmall',
    }));
    const [slideCount, setSlideCount,] = useState(0);
    const [activeSlide, setActiveSlide,] = useState(0);
    const [nextActiveSlide, setNextActiveSlide,] = useState(0);
    const [scrollDirection, setScrollDirection,] = useState('none');
    const slideGap = useMemo(() => (extraSmallMediaQueryState === 'ABOVE'
        ? slideGaps.medium
        : slideGaps.small), [
        extraSmallMediaQueryState,
    ]);
    const slideSize = useMemo(() => (extraSmallMediaQueryState === 'ABOVE'
        ? slideSizes.medium
        : slideSizes.small), [
        extraSmallMediaQueryState,
    ]);
    const isButtonVisible = useMemo(() => (extraSmallMediaQueryState === 'ABOVE'), [
        extraSmallMediaQueryState,
    ]);
    const slideOffset = useMemo(() => (styleUtils.rem(slideSize + styleUtils.remToPxValue(slideGap))), [
        slideGap,
        slideSize,
    ]);
    const [isMounted, setIsMounted,] = useState(false);
    useEffect(() => {
        setIsMounted(true);
    }, []);
    const buttonContainerStyles = useMemo(() => (css `
        align-self: center;
      `), []);
    const containerStyles = useMemo(() => (css `
        ${styleUtils.borderBox}
        flex-direction: row;
        justify-content: center;
        margin: 0;
        padding: 0;
        visibility: ${isMounted ? 'visible' : 'hidden'};

        ${isButtonVisible && css `
          display: inline-flex;

          ${orientation === 'horizontal' && css `
            column-gap: ${theme.spacing.base};
          `}
          ${orientation === 'vertical' && css `
            flex-direction: column;
            row-gap: ${theme.spacing.base};
          `}
        `}
      `), [
        isMounted,
        orientation,
        isButtonVisible,
    ]);
    const carouselContainerStyles = useMemo(() => (css `
        ${isButtonVisible && (orientation === 'horizontal') && css `
          width: ${styleUtils.rem(carouselContainerSize)};
        `}
        ${isButtonVisible && (orientation === 'vertical') && css `
          height: ${styleUtils.rem(carouselContainerSize)};
        `}

        ${theme.media.greaterThan('small')(css `
          overflow: hidden;
        `)}
      `), [
        isButtonVisible,
        orientation,
    ]);
    const slideListStyles = useMemo(() => (css `
        ${styleUtils.hiddenScrollbarStyles}
        display: flex;
        position: relative;
        flex-direction: row;

        @media (max-width: ${theme.breakpoints.small}) {
          overflow-x: auto;
        }

        ${isButtonVisible && (orientation === 'horizontal') && css `
          left: -${slideOffset};
          max-width: 100%;
          ${scrollDirection === 'forward' && css `
            transform: translate3D(-${slideOffset}, 0, 0);
          `}
          ${scrollDirection === 'backward' && css `
            transform: translate3D(${slideOffset}, 0, 0);
          `}
        `}
        ${isButtonVisible && (orientation === 'vertical') && css `
          top: -${slideOffset};
          flex-direction: column;
          ${scrollDirection === 'forward' && css `
            transform: translate3D(0, -${slideOffset}, 0);
          `}
          ${scrollDirection === 'backward' && css `
            transform: translate3D(0, ${slideOffset}, 0);
          `}
        `}
        ${scrollDirection !== 'none' && css `
          transition: transform 300ms ease-out;
        `}
      `), [
        isButtonVisible,
        slideOffset,
        orientation,
        scrollDirection,
    ]);
    const providerValue = useMemo(() => ({
        slideSize,
    }), [
        slideSize,
    ]);
    const onNextClick = useCallback(() => {
        setScrollDirection('forward');
        setNextActiveSlide((slide) => ((slide + 1) % slideCount));
    }, [
        slideCount,
    ]);
    const onPreviousClick = useCallback(() => {
        const nextTarget = ((activeSlide - 1) < 0
            ? slideCount - 1
            : activeSlide - 1);
        setScrollDirection('backward');
        setNextActiveSlide(nextTarget % slideCount);
    }, [
        activeSlide,
        slideCount,
    ]);
    const onSlideListTransitionEnd = useCallback(() => {
        setActiveSlide(nextActiveSlide);
        setScrollDirection('none');
    }, [
        nextActiveSlide,
    ]);
    const getSlideOrder = useCallback((slideIndex) => {
        if (extraSmallMediaQueryState !== 'ABOVE') {
            return null;
        }
        let refSlide = activeSlide - 1;
        if (refSlide < 0) {
            refSlide = slideCount + refSlide;
        }
        let order = slideIndex - refSlide;
        if (order < 0) {
            order = slideCount + order;
        }
        return order;
    }, [
        activeSlide,
        extraSmallMediaQueryState,
        slideCount,
    ]);
    useEffect(() => {
        setSlideCount(Children.count(children));
    }, [
        children,
    ]);
    return (_jsxs("div", Object.assign({ css: containerStyles }, { children: [isButtonVisible
                && (_jsx("div", Object.assign({ css: buttonContainerStyles }, { children: _jsx(IconButton, { iconType: orientation === 'horizontal' ? 'arrow-left' : 'arrow-up', onClick: onPreviousClick, size: "large", type: "secondary" }) }))), _jsx("div", Object.assign({ css: carouselContainerStyles }, { children: _jsx(Picker, Object.assign({ name: name, onChange: onChange, selectionType: "single", value: value }, { children: _jsx(ThumbnailCarouselContext.Provider, Object.assign({ value: providerValue }, { children: _jsx("div", Object.assign({ css: slideListStyles, onTransitionEnd: onSlideListTransitionEnd }, { children: Children.map(children, (child, index) => (_jsx("div", Object.assign({ css: css `
                          flex: 0 0 ${slideOffset};
                          order: ${getSlideOrder(index)};
                        ` }, { children: _jsx("div", Object.assign({ css: css `
                            width: fit-content;
                          ` }, { children: child })) }), `slide-${index}`))) })) })) })) })), isButtonVisible
                && (_jsx("div", Object.assign({ css: buttonContainerStyles }, { children: _jsx(IconButton, { iconType: orientation === 'horizontal' ? 'arrow-right' : 'arrow-down', onClick: onNextClick, size: "large", type: "secondary" }) })))] })));
};
const MemoizedThumbnailCarousel = memo(ThumbnailCarousel);
export { MemoizedThumbnailCarousel as ThumbnailCarousel };
// MC-1068 Temporary hack for Storybook.
export default ThumbnailCarousel;
